import * as THREE from 'three/webgpu';

import { mulberry32 } from '../math/rng.js';
import { triangulatePolygon, polygonArea } from './polygon.js';
import { assertNonIndexed } from './mergeGeo.js';

/**
 * @file THE accumulation funnel. Every static-geometry generator in the project
 * pushes through a `GeoBuilder`, and what comes out the other end is one merged
 * non-indexed geometry per MaterialKey — which is then chunked (`world/Chunker`)
 * and baked (`bake/bakeVertexLight`).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY IT LOOKS LIKE THIS
 * ─────────────────────────────────────────────────────────────────────────────
 * • Internally it accumulates **raw typed-array chunks per material key**. There
 *   is deliberately no per-face / per-vertex JS object anywhere: a city is
 *   ~220k vertices and object churn at that scale is the difference between a
 *   400ms build and a 4s one.
 * • Every triangle gets `tint x (1 +/- tintJitter)` written into the `color`
 *   attribute. That is the **ALBEDO**, not lighting. Read the handoff note at
 *   the bottom of this header before touching it.
 * • The tint jitter draws exactly ONE rng value per triangle, ALWAYS — even when
 *   no tint was passed and even when `tintJitter` is 0. That keeps the random
 *   stream independent of which options a caller happened to pass, so adding a
 *   `tint` to one call does not reshuffle every later prop in the level.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * HANDOFF TO T4 (bake)
 * ─────────────────────────────────────────────────────────────────────────────
 * Post-`merge()`, `color` holds LINEAR ALBEDO in 0..1 (tint x per-face jitter).
 * The bake is expected to:
 *   1. read `color` as `albedo`,
 *   2. OVERWRITE `color` with the day channel
 *      `albedo * (sun*1.35*NdL*sunVis + hemi*0.55*ao + bounce*0.22*ao + 0.05)`,
 *   3. ADD `color1` with the night channel.
 * Both stay Float32 x3 and may exceed 1.0 (that is what feeds bloom).
 *
 * @module engine/build/GeoBuilder
 */

/** @typedef {import('../types.js').MaterialKey} MaterialKey */
/** @typedef {import('../types.js').Bounds2} Bounds2 */
/** @typedef {import('../types.js').Rng} Rng */
/** @typedef {{ x: number, z: number, w: number, d: number }} Rect */
/** @typedef {THREE.Color|{r: number, g: number, b: number}|[number, number, number]|number} TintLike */

const _normalMat = new THREE.Matrix3();

/**
 * @param {TintLike|null|undefined} tint
 * @param {Float32Array} out length 3
 */
function readTint(tint, out) {
  if (tint == null) {
    out[0] = 1;
    out[1] = 1;
    out[2] = 1;
    return;
  }
  if (typeof tint === 'number') {
    out[0] = ((tint >> 16) & 255) / 255;
    out[1] = ((tint >> 8) & 255) / 255;
    out[2] = (tint & 255) / 255;
    return;
  }
  if (Array.isArray(tint)) {
    out[0] = tint[0];
    out[1] = tint[1];
    out[2] = tint[2];
    return;
  }
  out[0] = tint.r;
  out[1] = tint.g;
  out[2] = tint.b;
}

export class GeoBuilder {
  /**
   * @param {Object} [opts]
   * @param {Rng} [opts.rng] Seeded generator for the per-triangle tint jitter.
   *   STRONGLY prefer passing your stage's stream (`streamRng(seed,'buildings')`)
   *   — the fallback is a fixed constant seed, which is deterministic but shared
   *   by every builder that forgets.
   * @param {number} [opts.tintJitter=0.06] Per-TRIANGLE albedo jitter, +/-6%
   *   (the plan's `BakeConfig.tintJitter` default).
   * @param {MaterialKey} [opts.defaultMat='default']
   */
  constructor({ rng, tintJitter = 0.06, defaultMat = 'default' } = {}) {
    /** @type {Rng} */
    this.rng = typeof rng === 'function' ? rng : mulberry32(0x9e3779b9);
    this.tintJitter = tintJitter;
    this.defaultMat = defaultMat;

    /**
     * matKey -> accumulated chunks. `verts` is the running vertex total so
     * `merge()` can allocate exactly once.
     * @type {Map<MaterialKey, {chunks: Array<{pos: Float32Array, nor: Float32Array, uv: Float32Array, col: Float32Array}>, verts: number}>}
     */
    this._buckets = new Map();

    this._minX = Infinity;
    this._minY = Infinity;
    this._minZ = Infinity;
    this._maxX = -Infinity;
    this._maxY = -Infinity;
    this._maxZ = -Infinity;

    /** @type {Map<MaterialKey, THREE.BufferGeometry>|null} */
    this._merged = null;

    this._tmpTint = new Float32Array(3);
  }

  // ── accumulation ───────────────────────────────────────────────────────────

  /**
   * Appends a geometry, optionally transformed.
   *
   * @param {THREE.BufferGeometry} geo Source. Indexed input is expanded on the
   *   fly; the source is never modified and may be reused/disposed afterwards.
   * @param {THREE.Matrix4|null} [matrix] World transform. Normals go through the
   *   proper inverse-transpose, and a MIRRORING matrix (negative determinant)
   *   flips triangle winding so the result is still front-facing.
   * @param {{ mat?: MaterialKey, tint?: TintLike, tintJitter?: number }} [opts]
   * @returns {this}
   */
  add(geo, matrix = null, opts = {}) {
    const posA = /** @type {THREE.BufferAttribute|undefined} */ (geo?.getAttribute?.('position'));
    if (!posA || posA.count === 0) return this;

    const idx = geo.index ? geo.index.array : null;
    const n = idx ? idx.length : posA.count;
    if (n % 3 !== 0) throw new Error('GeoBuilder.add: vertex count is not a multiple of 3');

    const norA = /** @type {THREE.BufferAttribute|undefined} */ (geo.getAttribute('normal'));
    const uvA = /** @type {THREE.BufferAttribute|undefined} */ (geo.getAttribute('uv'));
    const P = posA.array;
    const N = norA ? norA.array : null;
    const U = uvA ? uvA.array : null;

    const pos = new Float32Array(n * 3);
    const nor = new Float32Array(n * 3);
    const uv = new Float32Array(n * 2);
    const col = new Float32Array(n * 3);

    // Transform setup.
    let e = null;
    let m3 = null;
    let mirrored = false;
    if (matrix) {
      e = matrix.elements;
      m3 = _normalMat.getNormalMatrix(matrix).elements;
      mirrored = matrix.determinant() < 0;
    }

    // Mirroring flips handedness: swap two corners so we stay CCW-outside.
    // Hoisted out of the loop — no per-triangle allocation, ever.
    const o1 = mirrored ? 2 : 1;
    const o2 = mirrored ? 1 : 2;

    for (let t = 0; t < n; t += 3) {
      for (let k = 0; k < 3; k++) {
        const local = t + (k === 0 ? 0 : k === 1 ? o1 : o2);
        const srcV = idx ? idx[local] : local;
        const sp = srcV * 3;
        const su = srcV * 2;
        const dp = (t + k) * 3;
        const du = (t + k) * 2;

        let x = P[sp];
        let y = P[sp + 1];
        let z = P[sp + 2];
        if (e) {
          const tx = e[0] * x + e[4] * y + e[8] * z + e[12];
          const ty = e[1] * x + e[5] * y + e[9] * z + e[13];
          const tz = e[2] * x + e[6] * y + e[10] * z + e[14];
          x = tx;
          y = ty;
          z = tz;
        }
        pos[dp] = x;
        pos[dp + 1] = y;
        pos[dp + 2] = z;
        if (x < this._minX) this._minX = x;
        if (x > this._maxX) this._maxX = x;
        if (y < this._minY) this._minY = y;
        if (y > this._maxY) this._maxY = y;
        if (z < this._minZ) this._minZ = z;
        if (z > this._maxZ) this._maxZ = z;

        if (N) {
          let nx = N[sp];
          let ny = N[sp + 1];
          let nz = N[sp + 2];
          if (m3) {
            const ax = m3[0] * nx + m3[3] * ny + m3[6] * nz;
            const ay = m3[1] * nx + m3[4] * ny + m3[7] * nz;
            const az = m3[2] * nx + m3[5] * ny + m3[8] * nz;
            nx = ax;
            ny = ay;
            nz = az;
          }
          const l = Math.hypot(nx, ny, nz) || 1;
          nor[dp] = nx / l;
          nor[dp + 1] = ny / l;
          nor[dp + 2] = nz / l;
        }
        if (U) {
          uv[du] = U[su];
          uv[du + 1] = U[su + 1];
        }
      }
      if (!N) writeFaceNormal(pos, nor, t);
    }

    this._writeTint(col, n, opts.tint, opts.tintJitter);
    this._push(opts.mat ?? this.defaultMat, pos, nor, uv, col);
    return this;
  }

  /**
   * Extrudes a footprint polygon from `y0` to `y1` straight into the buffers —
   * no intermediate `BufferGeometry`, which matters when a city emits thousands
   * of them.
   *
   * @param {Array<[number, number]>} poly World XZ, CCW (`polygonArea > 0`);
   *   clockwise is reversed for you.
   * @param {number} y0 Bottom.
   * @param {number} y1 Top.
   * @param {{ mat?: MaterialKey, tint?: TintLike, tintJitter?: number, uvScale?: number, caps?: 'both'|'top'|'none' }} [opts]
   *   `uvScale` multiplies the metre-based UVs (so `1/2.5` = a repeat every
   *   2.5m). `caps` defaults to `'both'`; pass `'top'` for buildings sitting on
   *   the ground to save a triangle fan per footprint.
   * @returns {this}
   */
  addPolyPrism(poly, y0, y1, opts = {}) {
    const src = polygonArea(poly) >= 0 ? poly : poly.slice().reverse();
    const np = src.length;
    if (np < 3) return this;

    const uvScale = opts.uvScale ?? 1;
    const caps = opts.caps ?? 'both';
    const tris = caps === 'none' ? [] : triangulatePolygon(src);
    const capCount = caps === 'both' ? 2 : caps === 'top' ? 1 : 0;
    const triTotal = np * 2 + tris.length * capCount;
    const n = triTotal * 3;

    const pos = new Float32Array(n * 3);
    const nor = new Float32Array(n * 3);
    const uv = new Float32Array(n * 2);
    const col = new Float32Array(n * 3);

    let w = 0; // vertex cursor
    const put = (x, y, z, nx, ny, nz, u, v) => {
      const p = w * 3;
      const q = w * 2;
      pos[p] = x;
      pos[p + 1] = y;
      pos[p + 2] = z;
      nor[p] = nx;
      nor[p + 1] = ny;
      nor[p + 2] = nz;
      uv[q] = u * uvScale;
      uv[q + 1] = v * uvScale;
      w++;
      if (x < this._minX) this._minX = x;
      if (x > this._maxX) this._maxX = x;
      if (y < this._minY) this._minY = y;
      if (y > this._maxY) this._maxY = y;
      if (z < this._minZ) this._minZ = z;
      if (z > this._maxZ) this._maxZ = z;
    };

    // Walls: outward normal (ez, 0, -ex) for a positively-wound polygon.
    let run = 0;
    for (let i = 0; i < np; i++) {
      const p = src[i];
      const q = src[(i + 1) % np];
      const ex = q[0] - p[0];
      const ez = q[1] - p[1];
      const len = Math.hypot(ex, ez) || 1;
      const nx = ez / len;
      const nz = -ex / len;
      const u0 = run;
      const u1 = run + len;
      // (p,y0) (p,y1) (q,y1) then (p,y0) (q,y1) (q,y0)
      put(p[0], y0, p[1], nx, 0, nz, u0, y0);
      put(p[0], y1, p[1], nx, 0, nz, u0, y1);
      put(q[0], y1, q[1], nx, 0, nz, u1, y1);
      put(p[0], y0, p[1], nx, 0, nz, u0, y0);
      put(q[0], y1, q[1], nx, 0, nz, u1, y1);
      put(q[0], y0, q[1], nx, 0, nz, u1, y0);
      run += len;
    }

    for (const [a, b, c] of tris) {
      const pa = src[a];
      const pb = src[b];
      const pc = src[c];
      // Positively-wound XZ triangles face -Y, so reverse for the top cap.
      put(pc[0], y1, pc[1], 0, 1, 0, pc[0], pc[1]);
      put(pb[0], y1, pb[1], 0, 1, 0, pb[0], pb[1]);
      put(pa[0], y1, pa[1], 0, 1, 0, pa[0], pa[1]);
      if (caps === 'both') {
        put(pa[0], y0, pa[1], 0, -1, 0, pa[0], pa[1]);
        put(pb[0], y0, pb[1], 0, -1, 0, pb[0], pb[1]);
        put(pc[0], y0, pc[1], 0, -1, 0, pc[0], pc[1]);
      }
    }

    this._writeTint(col, n, opts.tint, opts.tintJitter);
    this._push(opts.mat ?? this.defaultMat, pos, nor, uv, col);
    return this;
  }

  /**
   * Up-facing quad covering `rect` at height `y`. Ground slabs, sidewalks,
   * plazas, roof decks.
   * @param {Rect} rect `{x,z}` is the MIN corner (see polygon.js).
   * @param {number} y
   * @param {{ mat?: MaterialKey, tint?: TintLike, tintJitter?: number, uvScale?: number }} [opts]
   * @returns {this}
   */
  addPlate(rect, y, opts = {}) {
    const uvScale = opts.uvScale ?? 1;
    const x0 = rect.x;
    const z0 = rect.z;
    const x1 = rect.x + rect.w;
    const z1 = rect.z + rect.d;

    const pos = new Float32Array(6 * 3);
    const nor = new Float32Array(6 * 3);
    const uv = new Float32Array(6 * 2);
    const col = new Float32Array(6 * 3);

    // (x0,z1) (x1,z1) (x1,z0) / (x0,z1) (x1,z0) (x0,z0)  => +Y
    const verts = [
      [x0, z1],
      [x1, z1],
      [x1, z0],
      [x0, z1],
      [x1, z0],
      [x0, z0],
    ];
    for (let i = 0; i < 6; i++) {
      const p = i * 3;
      const q = i * 2;
      pos[p] = verts[i][0];
      pos[p + 1] = y;
      pos[p + 2] = verts[i][1];
      nor[p + 1] = 1;
      uv[q] = (verts[i][0] - x0) * uvScale;
      uv[q + 1] = (verts[i][1] - z0) * uvScale;
    }
    if (x0 < this._minX) this._minX = x0;
    if (x1 > this._maxX) this._maxX = x1;
    if (z0 < this._minZ) this._minZ = z0;
    if (z1 > this._maxZ) this._maxZ = z1;
    if (y < this._minY) this._minY = y;
    if (y > this._maxY) this._maxY = y;

    this._writeTint(col, 6, opts.tint, opts.tintJitter);
    this._push(opts.mat ?? this.defaultMat, pos, nor, uv, col);
    return this;
  }

  // ── output ─────────────────────────────────────────────────────────────────

  /**
   * Concatenates each material bucket into one non-indexed geometry.
   * Result is memoised — calling `merge()` twice returns the SAME map and the
   * SAME geometries. Any `add*` after a merge invalidates the cache.
   *
   * @returns {Map<MaterialKey, THREE.BufferGeometry>} `.index === null` on every
   *   geometry; each carries `userData.materialKey`.
   */
  merge() {
    if (this._merged) return this._merged;
    /** @type {Map<MaterialKey, THREE.BufferGeometry>} */
    const out = new Map();

    for (const [key, bucket] of this._buckets) {
      const total = bucket.verts;
      if (!total) continue;
      const pos = new Float32Array(total * 3);
      const nor = new Float32Array(total * 3);
      const uv = new Float32Array(total * 2);
      const col = new Float32Array(total * 3);
      let o3 = 0;
      let o2 = 0;
      for (const c of bucket.chunks) {
        pos.set(c.pos, o3);
        nor.set(c.nor, o3);
        col.set(c.col, o3);
        uv.set(c.uv, o2);
        o3 += c.pos.length;
        o2 += c.uv.length;
      }
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
      g.setAttribute('normal', new THREE.BufferAttribute(nor, 3));
      g.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
      g.setAttribute('color', new THREE.BufferAttribute(col, 3));
      g.computeBoundingBox();
      g.computeBoundingSphere();
      g.userData.materialKey = key;
      assertNonIndexed(g, `GeoBuilder.merge(${key})`);
      out.set(key, g);
    }

    this._merged = out;
    return out;
  }

  /**
   * Drops the accumulated chunks (the merged geometries keep the data). Call it
   * after `merge()` on a big level to hand ~2x the geometry memory back before
   * the bake allocates its own buffers.
   * @returns {this}
   */
  release() {
    this._buckets.clear();
    return this;
  }

  /**
   * @returns {Bounds2} XZ extent plus `minY`/`maxY`. All-zero when nothing was
   *   added (so callers can hand it to `CollisionGrid`/`Chunker` blindly).
   */
  bounds() {
    if (this._minX === Infinity) return { minX: 0, minZ: 0, maxX: 0, maxZ: 0, minY: 0, maxY: 0 };
    return {
      minX: this._minX,
      minZ: this._minZ,
      maxX: this._maxX,
      maxZ: this._maxZ,
      minY: this._minY,
      maxY: this._maxY,
    };
  }

  /**
   * @returns {{verts: number, tris: number, materials: number, chunks: number, byMaterial: Object<string, {verts: number, tris: number, chunks: number}>, bounds: Bounds2}}
   */
  stats() {
    /** @type {Object<string, {verts: number, tris: number, chunks: number}>} */
    const byMaterial = {};
    let verts = 0;
    let chunks = 0;
    for (const [key, bucket] of this._buckets) {
      byMaterial[key] = {
        verts: bucket.verts,
        tris: bucket.verts / 3,
        chunks: bucket.chunks.length,
      };
      verts += bucket.verts;
      chunks += bucket.chunks.length;
    }
    return {
      verts,
      tris: verts / 3,
      materials: this._buckets.size,
      chunks,
      byMaterial,
      bounds: this.bounds(),
    };
  }

  // ── internals ──────────────────────────────────────────────────────────────

  /**
   * @param {Float32Array} col
   * @param {number} n Vertex count (multiple of 3).
   * @param {TintLike|null|undefined} tint
   * @param {number|undefined} jitterOverride
   */
  _writeTint(col, n, tint, jitterOverride) {
    const t = this._tmpTint;
    readTint(tint, t);
    const amt = jitterOverride ?? this.tintJitter;
    const rng = this.rng;
    for (let i = 0; i < n; i += 3) {
      // ALWAYS draw, even when amt === 0 — keeps the stream option-independent.
      const j = 1 + (rng() * 2 - 1) * amt;
      const r = t[0] * j;
      const g = t[1] * j;
      const b = t[2] * j;
      for (let v = 0; v < 3; v++) {
        const o = (i + v) * 3;
        col[o] = r;
        col[o + 1] = g;
        col[o + 2] = b;
      }
    }
  }

  /**
   * @param {MaterialKey} key
   * @param {Float32Array} pos
   * @param {Float32Array} nor
   * @param {Float32Array} uv
   * @param {Float32Array} col
   */
  _push(key, pos, nor, uv, col) {
    let bucket = this._buckets.get(key);
    if (!bucket) {
      bucket = { chunks: [], verts: 0 };
      this._buckets.set(key, bucket);
    }
    bucket.chunks.push({ pos, nor, uv, col });
    bucket.verts += pos.length / 3;
    this._merged = null;
  }
}

/**
 * @param {Float32Array} pos
 * @param {Float32Array} nor
 * @param {number} t First vertex index of the triangle.
 */
function writeFaceNormal(pos, nor, t) {
  const a = t * 3;
  const b = (t + 1) * 3;
  const c = (t + 2) * 3;
  const ux = pos[b] - pos[a];
  const uy = pos[b + 1] - pos[a + 1];
  const uz = pos[b + 2] - pos[a + 2];
  const vx = pos[c] - pos[a];
  const vy = pos[c + 1] - pos[a + 1];
  const vz = pos[c + 2] - pos[a + 2];
  let nx = uy * vz - uz * vy;
  let ny = uz * vx - ux * vz;
  let nz = ux * vy - uy * vx;
  const l = Math.hypot(nx, ny, nz) || 1;
  nx /= l;
  ny /= l;
  nz /= l;
  for (let v = 0; v < 3; v++) {
    const o = (t + v) * 3;
    nor[o] = nx;
    nor[o + 1] = ny;
    nor[o + 2] = nz;
  }
}
